import { FC, useContext, useReducer } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Button } from 'src/components/button';

import { bookingDetailsReducer } from '../../booking-summary/booking-details-reducer';
import { useGetSelfServeAmendDetails } from '../../../lib/hooks/use-get-self-serve-amend-details';
import { routes } from '../../../constants/routes';
import { LoginDialog } from '../../../features/login-dialog';
import { AuthContext } from '../../../providers/auth-context';
import { styles, amendSummaryStyles } from '../styles';
import { PaymentPlanReducerTypes } from '../../booking-summary/types';
import { Term } from '../../../shared/types/booking-types';
import { InstalmentType } from '../../../shared/enum/InstalmentType';
import { useFetchCreatePaymentPlan } from '../../../lib/hooks/use-create-payment-plan';
import { BookingDetailsSkeleton } from '../../../features/booking-summary/booking-details/booking-details-skeleton';
import { Currency } from '../../../shared/enum/Currency';
import { PaymentPlanView } from '../../../features/payment-plan/payment-plan-view';
import { usePostSelfServeAmend } from '../../../lib/hooks/use-post-self-serve-amend';
import { ProcessSelfServeAmendDto } from '../../../api/amendService/dto/ProcessSelfServeAmend.dto';
import { ErrorSnackbarContext } from '../../../features/error-snackbar/error-snackbar-context';
import { ErrorSnackbarActionType } from '../../../features/error-snackbar/types';

import { AmendBookingDetailsView } from './amend-booking-details-view';

export const AmendBookingSummary: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const userState = useContext(AuthContext);
    const dispatchSnackbarError = useContext(ErrorSnackbarContext);
    const [searchParams] = useSearchParams();
    const [userChoices, dispatchUserChoices] = useReducer(bookingDetailsReducer, {
        numberOfStudents: 1,
        term: null,
        instalmentType: null,
        instalmentId: null,
    });

    const paymentPlanChoices = { ...userChoices, term: userChoices.term?.name ?? null };

    const bookingId = searchParams.get('bookingId');

    const {
        data: selfServeAmendDetails,
        error,
        isFetching: fetchingPaymentOptions,
    } = useGetSelfServeAmendDetails(bookingId);
    const {
        data: createdPaymentPlan,
        isFetching: fetchingPaymentPlan,
        isError: failedToCreatePaymentPlan,
    } = useFetchCreatePaymentPlan({
        userChoices: paymentPlanChoices,
        promotionId: null,
        isRebooker: false,
        fillerWeekEligible: false,
        roomData: selfServeAmendDetails?.room ?? null,
        bookingId: bookingId ?? null,
        amend: true,
        adminFee: true,
    });
    const { mutate: postSelfServeAmend, isPending: isPostingSelfServeAmend } =
        usePostSelfServeAmend();

    const isOriaRoom = selfServeAmendDetails?.room?.room_code.includes('OR1');

    if (!bookingId || error) {
        return <Navigate to={routes.failure.index} />;
    }

    if (!userState.loggedIn) {
        return (
            <Box css={styles.layout}>
                <Box css={styles.leftSide}>
                    <LoginDialog open closeFn={() => {}} />
                </Box>
            </Box>
        );
    }

    const handleTermChange = (value: Term) => {
        dispatchUserChoices({ type: PaymentPlanReducerTypes.SET_TERM, data: value });
        const selectedInstalment = value.instalments.find(
            (instalment) => instalment.name === userChoices.instalmentType
        );

        const instalmentName = selectedInstalment
            ? selectedInstalment.name
            : value.instalments[0].name;
        const instalmentId = selectedInstalment ? selectedInstalment.id : value.instalments[0].id;

        dispatchUserChoices({
            type: PaymentPlanReducerTypes.SET_INSTALMENT_TYPE,
            data: instalmentName as InstalmentType,
        });
        dispatchUserChoices({
            type: PaymentPlanReducerTypes.SET_INSTALMENT_ID,
            data: instalmentId,
        });
    };

    const handleInstalmentChange = (value: string | null, id: string | null) => {
        dispatchUserChoices({
            type: PaymentPlanReducerTypes.SET_INSTALMENT_TYPE,
            data: value as InstalmentType,
        });
        dispatchUserChoices({ type: PaymentPlanReducerTypes.SET_INSTALMENT_ID, data: id });
    };

    const handleSubmit = () => {
        if (!userChoices.instalmentId || !createdPaymentPlan?.weeklyPrice) {
            dispatchSnackbarError({
                type: ErrorSnackbarActionType.SOMETHING_WENT_WRONG,
                payload: null,
            });
            return;
        }

        const payload = new ProcessSelfServeAmendDto(
            userChoices.instalmentId as string,
            createdPaymentPlan.weeklyPrice
        );

        postSelfServeAmend(
            { payload, bookingId },
            {
                onSuccess: (res) => {
                    const url = new URL(res, window.location.href);
                    url.searchParams.set('selfServe', 'true');
                    window.location.href = url.toString();
                },
                onError: () => navigate(routes.failure.index),
            }
        );
    };

    return (
        <Box css={amendSummaryStyles.layout}>
            <Box css={amendSummaryStyles.wrapper}>
                <Typography variant="h1" css={amendSummaryStyles.title}>
                    {t('amend_booking_summary_title')}
                </Typography>

                {fetchingPaymentOptions || !selfServeAmendDetails ? (
                    <BookingDetailsSkeleton />
                ) : (
                    <AmendBookingDetailsView
                        handleTermChange={handleTermChange}
                        handleInstalmentChange={handleInstalmentChange}
                        roomData={selfServeAmendDetails.room}
                        terms={selfServeAmendDetails.payment_options}
                        userChoices={userChoices}
                        loading={fetchingPaymentOptions}
                    />
                )}
                <PaymentPlanView
                    createdPaymentPlan={createdPaymentPlan}
                    loading={fetchingPaymentPlan}
                    currency={selfServeAmendDetails?.room.currency as Currency}
                    showSantanderCards={isOriaRoom}
                />

                <Button
                    variant="contained"
                    css={amendSummaryStyles.cta}
                    onClick={handleSubmit}
                    loading={isPostingSelfServeAmend}
                    disabled={
                        failedToCreatePaymentPlan ||
                        !userChoices.instalmentId ||
                        fetchingPaymentPlan
                    }
                    id="submit-button"
                >
                    {t('booking_summary_payment_cta')}
                </Button>
            </Box>
        </Box>
    );
};
